import { useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Avatar, 
  Paper, 
  Grid, 
  Divider,
  Button,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(15),
  height: theme.spacing(15),
  marginBottom: theme.spacing(2),
}));

const PLAN_TIERS: { [key: string]: { name: string; tier: number } } = {
  'price_1RAicZFiHfTOO8HlMixz6XNB': { name: 'Enterprise', tier: 3 },
  'price_1RAicYFiHfTOO8Hl8CWXBiZG': { name: 'Pro', tier: 2 },
  'price_1RAicWFiHfTOO8Hl6bi1nuxK': { name: 'Basic', tier: 1 }
};

const getHighestPlanName = (planId: string): string => {
  // If no plan ID, return 'Free Member'
  if (!planId) return 'Free Member';
  
  // Get the current plan's tier
  const currentPlan = PLAN_TIERS[planId];
  
  // If the plan ID doesn't match any known plans, return 'Basic'
  if (!currentPlan) return 'Basic';
  
  // Return the plan name
  return currentPlan.name;
};

const Profile = () => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading || !user) {
    return (
      <Container>
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Box sx={{ py: 4 }}>
            <StyledPaper elevation={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <ProfileAvatar>
                  {user.name[0].toUpperCase()}
                </ProfileAvatar>
                <Typography variant="h4" gutterBottom>
                  {user.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {user.email}
                </Typography>
                <Chip 
                  label={
                    user.subscription.status === 'active' && user.subscription.plan
                      ? `${getHighestPlanName(user.subscription.plan)} Plan`
                      : user.subscription.status === 'trialing'
                      ? 'Trial Member'
                      : 'Free Member'
                  }
                  color={user.subscription.status === 'active' ? 'primary' : 'default'}
                  sx={{ 
                    mt: 1,
                    px: 2,
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderRadius: '16px',
                    height: '32px',
                    ...(user.subscription.status === 'active' && {
                      background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                      boxShadow: '0 2px 8px rgba(110, 58, 228, 0.3)',
                    }),
                    ...(user.subscription.status === 'trialing' && {
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                    })
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{user.tokenBalance}</Typography>
                    <Typography color="text.secondary">Available Tokens</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => navigate('/settings')}
                >
                  Edit Profile
                </Button>
              </Box>
            </StyledPaper>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default Profile; 